/**
 * Money and calendar dates, as the CRM screens show them.
 *
 * Not in core's `utils/format`: nothing else shows money, and core's
 * `formatDate` takes an instant, where these dates are calendar days with no
 * time zone — `new Date('2026-10-20')` is UTC midnight, which is the 19th
 * anywhere west of Greenwich.
 *
 * Like core's formatters, these read the active language from the i18next
 * singleton, so a component using them needs `useTranslation()` to re-render
 * when the language changes.
 */
import type { MoneyTotalDto } from '@forsys/shared';
import { currentLanguage } from '../../i18n';

/**
 * With the currency's own decimals — two for CZK, none for JPY, three for KWD
 * — which is what `Intl` does for a currency unless told otherwise. Capping it
 * at two would throw for KWD, whose minimum is three.
 */
export function formatMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(currentLanguage(), { style: 'currency', currency }).format(value);
  } catch {
    // An ISO code Intl does not know still has to read as money.
    return `${value} ${currency}`;
  }
}

/** One total per currency, joined: "120 000 Kč · 4 000 €". */
export function formatTotals(totals: readonly MoneyTotalDto[], field: 'value' | 'weightedValue' = 'value'): string {
  return totals.map((total) => formatMoney(total[field], total.currency)).join(' · ');
}

/** A `YYYY-MM-DD` calendar day, read as a local date. */
export function formatDay(day: string | null | undefined, empty = ''): string {
  if (!day) return empty;
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return day;
  return new Date(year, month - 1, date).toLocaleDateString(currentLanguage(), { dateStyle: 'medium' });
}

/** Overdue, due today, or later — against the caller's own calendar. */
export function dueState(day: string, todayIs: string): 'overdue' | 'today' | 'upcoming' {
  if (day < todayIs) return 'overdue';
  return day === todayIs ? 'today' : 'upcoming';
}
